import { useCallback, useState } from "react";
import { useHomeSwitchStore } from "src/store/homeSwitch.store";
/**
 * 트리 구조에서 isDefault: true인 리프 노드 찾기
 * 초기 로드 시 기본 선택 교재를 찾는 데 사용
 */ export const findDefaultBook = (items)=>{
    for (const item of items){
        if (item.sourceId && item.isDefault) return item;
        if (item.children) {
            const found = findDefaultBook(item.children);
            if (found) return found;
        }
    }
    return null;
};
/**
 * 트리 구조에서 특정 sourceId를 가진 노드 찾기
 * 사용자가 필터에서 교재를 선택한 후 해당 교재 정보를 찾는 데 사용
 */ export const findBySourceId = (items, sourceId)=>{
    for (const item of items){
        if (item.sourceId === sourceId) return item;
        if (item.children) {
            const found = findBySourceId(item.children, sourceId);
            if (found) return found;
        }
    }
    return null;
};
/**
 * 트리에서 선택된 교재 정보 계산
 * ipSelectSourceId가 있으면 해당 교재, 없으면 isDefault 교재 반환
 */ export const calculateSelectedBook = (items, ipSelectSourceId)=>{
    if (!items) return null;
    const targetItem = ipSelectSourceId ? findBySourceId(items, ipSelectSourceId) : findDefaultBook(items);
    if (!targetItem) return null;
    var _targetItem_title, _targetItem_publisher, _targetItem_thumbnailUrl;
    return {
        sourceId: targetItem.sourceId,
        sourceType: targetItem.sourceType,
        title: (_targetItem_title = targetItem.title) !== null && _targetItem_title !== void 0 ? _targetItem_title : targetItem.label,
        publisher: (_targetItem_publisher = targetItem.publisher) !== null && _targetItem_publisher !== void 0 ? _targetItem_publisher : "",
        thumbnail: (_targetItem_thumbnailUrl = targetItem.thumbnailUrl) !== null && _targetItem_thumbnailUrl !== void 0 ? _targetItem_thumbnailUrl : ""
    };
};
/**
 * Home 페이지의 IP Select 상태 관리 훅
 *
 * 역할:
 * - ipSelectSourceId 상태 관리 (API 쿼리 파라미터로 사용)
 * - 기본 교재 선택 상태 추적 (툴팁 표시 조건)
 *
 * 주의: selectedBook 계산은 별도로 calculateSelectedBook 유틸 사용
 * (API 응답 items가 필요하므로 훅 외부에서 계산)
 *
 * 데이터 흐름:
 * 1. useHomeIpSelect() → ipSelectSourceId 상태 관리
 * 2. useGetHomeSection({ ipSelectSourceId }) → API 호출
 * 3. calculateSelectedBook(items, ipSelectSourceId) → selectedBook 계산
 * 4. 필터 선택 시 handleSelectSource → ipSelectSourceId 변경 → 2번부터 반복
 *
 */ export const useHomeIpSelect = ()=>{
    // 사용자가 필터에서 선택한 교재의 sourceId
    // undefined면 기본 교재(isDefault: true)가 선택된 상태
    const [ipSelectSourceId, setIpSelectSourceId] = useState(null);
    // 과목(homeId) 변경 시 이전 선택 교재를 초기화 (안 하면 새 트리에서 매칭 실패 → 스켈레톤 고착).
    // 렌더 단계 보정으로 같은 렌더에서 null로 fetch → 중복 요청 방지.
    const homeId = useHomeSwitchStore((s)=>s.homeId);
    const [trackedHomeId, setTrackedHomeId] = useState(homeId);
    if (trackedHomeId !== homeId) {
        setTrackedHomeId(homeId);
        setIpSelectSourceId(null);
    }
    // 필터에서 교재 선택 시 호출되는 콜백
    // IpSelectSection → useIpSelect → onSelect → 이 함수
    const handleSelectSource = useCallback((source)=>{
        setIpSelectSourceId(source.sourceId);
    }, []);
    // 기본 교재가 선택된 상태인지 (툴팁 노출 조건)
    const isDefaultSelected = !ipSelectSourceId;
    return {
        /** API 쿼리 파라미터로 사용할 sourceId */ ipSelectSourceId,
        /** 필터에서 교재 선택 시 콜백 (IpSelectSection에 전달) */ handleSelectSource,
        /** 기본 교재 선택 상태 (툴팁 표시 조건) */ isDefaultSelected
    };
};
